'use client';

import {
  createContext,
  useState,
  useCallback,
  type PropsWithChildren,
} from 'react';

import type { MainDialogProps } from '@/components/dialogs/mainDialog';
import type { PanelTypes } from '@/components/panels/panelsMap';

export enum PanelSize {
  Full,
  Large,
  Small,
}

export interface SidePanelStore {
  isOpen: boolean;
  isAbsolute: boolean;
  Component: PanelTypes | undefined;
  panelSize: PanelSize | string;
  setSize: (size: PanelSize | number) => void;
  setIsOpen: (
    isOpen: boolean,
    size?: PanelSize | string,
    isAbsolute?: boolean
  ) => void;
  setComponent: (component: PanelTypes) => void;
}

export interface SnackbarStore {
  isOpen: boolean;
  message: React.ReactNode;
  isError?: boolean;
  isWarning?: boolean;
  showMessage: (message: React.ReactNode) => void;
  showErrorMessage: (message: React.ReactNode) => void;
  showWarningMessage: (message: React.ReactNode) => void;
  hide: () => void;
}

type DialogOptions = Omit<MainDialogProps, 'isOpen' | 'children'>;

interface DialogEntry extends DialogOptions {
  id: string;
  content: React.ReactNode;
}

interface DialogStore {
  stack: DialogEntry[];
  open: (id: string, content: React.ReactNode, options?: DialogOptions) => void;
  back: () => void;
  closeAll: () => void;
  replace: (
    id: string,
    content: React.ReactNode,
    options?: DialogOptions
  ) => void;
  isOpen: (id?: string) => boolean;
}

export const WorkspaceContext = createContext<{
  leftPanel: SidePanelStore;
  rightPanel: SidePanelStore;
  snackbar: SnackbarStore;
  sharedDialog: DialogStore;
} | null>(null);

export const WorkspaceProvider = ({ children }: PropsWithChildren) => {
  const [dialogStack, setDialogStack] = useState<DialogEntry[]>([]);

  const openDialog = useCallback(
    (id: string, content: React.ReactNode, options?: DialogOptions) => {
      setDialogStack((stack) => [
        ...stack,
        { isLoading: false, ...options, id, content },
      ]);
    },
    []
  );

  const backDialog = useCallback(() => {
    setDialogStack((stack) => stack.slice(0, -1));
  }, []);

  const closeAllDialogs = useCallback(() => {
    setDialogStack([]);
  }, []);

  const replaceDialog = useCallback(
    (id: string, content: React.ReactNode, options?: DialogOptions) => {
      setDialogStack((stack) => [
        ...stack.slice(0, -1),
        { isLoading: false, ...options, id, content },
      ]);
    },
    []
  );

  const isDialogOpen = useCallback(
    (id?: string) => {
      if (id) return dialogStack.some((d) => d.id === id);
      return dialogStack.length > 0;
    },
    [dialogStack]
  );

  const sharedDialog: DialogStore = {
    stack: dialogStack,
    open: openDialog,
    back: backDialog,
    closeAll: closeAllDialogs,
    replace: replaceDialog,
    isOpen: isDialogOpen,
  };

  const [leftPanel, setLeftPanel] = useState<SidePanelStore>({
    isOpen: false,
    isAbsolute: false,
    Component: undefined,
    panelSize: PanelSize.Large,
    setSize: (panelSize: PanelSize | number) =>
      setLeftPanel((state) => ({ ...state, panelSize })),
    setIsOpen: (isOpen: boolean, panelSize?: PanelSize | string) =>
      setLeftPanel((state) => ({
        ...state,
        isOpen,
        panelSize: panelSize ?? PanelSize.Large,
      })),
    setComponent: (Component: PanelTypes) =>
      setLeftPanel((state) => ({ ...state, Component })),
  });

  const [rightPanel, setRightPanel] = useState<SidePanelStore>({
    isOpen: false,
    isAbsolute: false,
    Component: undefined,
    panelSize: PanelSize.Large,
    setSize: (panelSize: PanelSize | number) =>
      setRightPanel((state) => ({ ...state, panelSize })),
    setIsOpen: (
      isOpen: boolean,
      panelSize?: PanelSize | string,
      isAbsolute?: boolean
    ) =>
      setRightPanel((state) => ({
        ...state,
        isOpen,
        isAbsolute: isAbsolute ?? false,
        panelSize: panelSize ?? PanelSize.Large,
      })),
    setComponent: (Component: PanelTypes) =>
      setRightPanel((state) => ({ ...state, Component })),
  });

  const [snackbar, setSnackbar] = useState<{
    isOpen: boolean;
    message: React.ReactNode;
    showMessage: (message: React.ReactNode) => void;
    showErrorMessage: (message: React.ReactNode) => void;
    showWarningMessage: (message: React.ReactNode) => void;
    hide: () => void;
  }>({
    isOpen: false,
    message: '',
    showMessage: (message: React.ReactNode) =>
      setSnackbar((state) => ({
        ...state,
        isOpen: true,
        message,
      })),
    showErrorMessage: (message: React.ReactNode) =>
      setSnackbar((state) => ({
        ...state,
        isOpen: true,
        message,
        isError: true,
      })),
    showWarningMessage: (message: React.ReactNode) =>
      setSnackbar((state) => ({
        ...state,
        isOpen: true,
        message,
        isWarning: true,
      })),
    hide: () =>
      setSnackbar((state) => ({
        ...state,
        isOpen: false,
        message: '',
      })),
  });

  return (
    <WorkspaceContext.Provider
      value={{ leftPanel, rightPanel, snackbar, sharedDialog }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};
